/**
 * Agent Types
 *
 * Types for agent definitions, summaries, and configuration.
 */

import type { HarnessId } from '../harness/ids.js';
import { KNOWN_MODEL_IDS, resolveCapabilities, type ModelProvider } from '../models/capabilities.js';
import { engineAllowsModel } from '../engines/models.js';

/**
 * Agent slug is dynamic - loaded from .claude/agents/ directory or database
 */
export type AgentSlug = string;

/**
 * SDK-compatible agent definition
 * Matches the format used by Claude Agent SDK for subagents in .claude/agents/
 */
export interface AgentDefinition {
  /** Unique slug for @mentions */
  slug: string;
  /** Display name */
  name: string;
  /** Description of what this agent does */
  description: string;
  /** Agent's system prompt / instructions */
  prompt: string;
  /** Tools available to this agent */
  tools: string[];
  /** Model to use (haiku, sonnet, opus, or inherit) */
  model: 'haiku' | 'sonnet' | 'opus' | 'inherit';
  /** Skills to preload into this agent (skill slugs) */
  skills: string[];
  /** MCP servers available to this agent (mcp slugs) */
  mcps: string[];
  /** Scope: system, user, or project */
  scope: 'system' | 'user' | 'project';
  /** Project ID if scope is project */
  projectId?: string;
  /** Max SDK turns for this agent (overrides default) */
  maxTurns?: number;
  /** Permission mode for this agent */
  permissionMode?: 'default' | 'acceptEdits' | 'dontAsk' | 'bypassPermissions' | 'plan';
  /** Memory mode — only 'project' works in Bash (persistent volume) */
  memory?: 'project';
  /** Whether this agent runs in the background (no streaming to UI) */
  background?: boolean;
  /** Isolation strategy — 'worktree' creates a git worktree for the agent */
  isolation?: 'worktree';
  /** UI metadata for display */
  metadata?: {
    category?: string;
    icon?: string;
    color?: string;
  };
}

/**
 * Agent summary for UI display
 */
export interface AgentSummary {
  slug: string;
  name: string;
  description: string;
  /** Skills attached to this agent */
  skills?: string[];
  /** MCP servers available to this agent */
  mcps?: string[];
  /** Category for grouping agents in UI (e.g., 'coding', 'research', 'devops') */
  category?: string;
  /** Icon identifier for display (e.g., 'code', 'search', 'terminal') */
  icon?: string;
  /** Color for UI display */
  color?: string;
  /** Whether this is a system agent (read-only) */
  isSystem?: boolean;
}

/**
 * Public agent summary for library browsing
 */
export interface PublicAgentSummary {
  slug: string;
  name: string;
  description: string;
  category?: string;
  icon?: string;
  color?: string;
  skills?: string[];
  mcps?: string[];
  authorId?: string;
  authorName?: string;
  authorAvatarUrl?: string;
  sourceUrl?: string;
  downloadCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Agent port - single process model
 * SDK manages subagents internally via Task tool (MAX_CONCURRENT_QUERIES = 30)
 */
export const AGENT_PORT = 3001;

/**
 * Agent execution modes — the user-facing quality TIER (Lite / Pro / Max),
 * mapped per-harness to that harness's own models. The user picks a HARNESS
 * (runtime) AND a TIER (mode); the (runtime, mode) pair resolves to a model.
 */
export type AgentMode = 'light' | 'pro' | 'max';

/** Claude tier → model. */
export const AGENT_MODE_MODELS: Record<AgentMode, string> = {
  light: 'claude-haiku-5-5', // Haiku 5.5 since 2026-10-07 (Haiku 4.5 removed)
  pro: 'claude-sonnet-5-5', // Sonnet 5.5 since 2026-09-29 (Sonnet 5 retired)
  max: 'claude-opus-4-6',
} as const;

/**
 * OpenAI tier → model. Mirrors the Claude tiers (light=fast/cheap,
 * pro=frontier workhorse, max=newest frontier). GPT-5.6 lineup per founder
 * (2026-07-09; account entitlement verified live 2026-07-10): light=Luna,
 * pro=Terra, max=Sol. GPT-6 since 2026-09-23: Luna and Sol move up, and Terra's
 * pro tier goes to GPT-6 Sol (Terra retired; Sol undercuts it on every bucket).
 * GPT-6.1 Sol replaces GPT-6 Sol on both tiers since 2026-09-30.
 * Bare aliases (no date suffix) auto-track the latest snapshot.
 */
export const OPENAI_MODE_MODELS: Record<AgentMode, string> = {
  light: 'gpt-6-luna',
  pro: 'gpt-6.1-sol',
  max: 'gpt-6.1-sol',
} as const;

/**
 * OpenAI Responses API tier → model. Mirrors OPENAI_MODE_MODELS — same GPT-5.x
 * tiers. The openai_responses arm uses the raw OpenAI Responses API (not the
 * @openai/agents SDK); the model aliases are identical but the wire protocol differs.
 */
export const OPENAI_RESPONSES_MODE_MODELS: Record<AgentMode, string> = {
  light: 'gpt-6-luna',
  pro: 'gpt-6.1-sol',
  max: 'gpt-6.1-sol',
} as const;

/**
 * Native (Vestra) tier → model. The native harness runs EVERY provider, so its
 * unpinned tiers are a product choice, not a provider mirror. Founder 2026-08-04:
 * the untouched default is Luna UNIVERSALLY — every unpinned native-arm
 * resolution (interactive send, server continuation, routine fire, initiative
 * push) lands here, so this one row is the "default model" for all of them.
 * An explicit per-task pin always wins over this map (pill = user intent).
 * light/max keep the Claude tiers until a per-user setting exists.
 */
export const NATIVE_MODE_MODELS: Record<AgentMode, string> = {
  light: AGENT_MODE_MODELS.light,
  // Founder 2026-08-25: Sol is the universal default (supersedes the 08-04
  // Luna-universal ruling). This row is the CODE FALLBACK; the live value is
  // DB-managed (runtime_config.default_model via resolveConfiguredAgentModel).
  // GPT-6 Sol since 2026-09-23 (successor to 5.6 Sol), GPT-6.1 Sol since 2026-09-30.
  pro: 'gpt-6.1-sol',
  max: AGENT_MODE_MODELS.max,
} as const;

/** Per-harness tier→model maps. */
export const MODE_MODELS_BY_RUNTIME: Record<HarnessId, Record<AgentMode, string>> = {
  claude: AGENT_MODE_MODELS,
  openai: OPENAI_MODE_MODELS,
  native: NATIVE_MODE_MODELS,
  openai_responses: OPENAI_RESPONSES_MODE_MODELS, // raw OpenAI Responses API arm (gated off by default)
  // The Claude Code engine runs the user's own Claude plan. Sonnet on every tier in v1:
  // it is available on every plan (Opus is not), and a model picker is a NEXT.
  // Sonnet 5.5 since 2026-09-29 (Sonnet 5 retired).
  claude_code: { light: 'claude-sonnet-5-5', pro: 'claude-sonnet-5-5', max: 'claude-sonnet-5-5' },
  // The Codex engine runs the user's own ChatGPT plan: its CLI's default model on every tier
  // (engines T3). Every id here is on ENGINE_MODEL_IDS.codex.
  codex: { light: 'gpt-6.1-sol', pro: 'gpt-6.1-sol', max: 'gpt-6.1-sol' },
} as const;

export const DEFAULT_AGENT_MODE: AgentMode = 'pro';

/**
 * Resolve a (mode, runtime) pair to a model ID. `runtime` MUST be the COERCED
 * LAUNCH runtime (what actually runs), not the stored intent. Since the
 * native-only ruling every launch runtime IS 'native', so the default matches
 * it; the legacy maps below are kept only to satisfy the HarnessId-keyed Record
 * for historical rows. Defaults: runtime→native, mode→pro.
 */
export function resolveAgentModel(mode: string | undefined, runtime: HarnessId = 'native'): string {
  const map = MODE_MODELS_BY_RUNTIME[runtime] ?? AGENT_MODE_MODELS;
  if (mode && mode in map) {
    return map[mode as AgentMode];
  }
  return map[DEFAULT_AGENT_MODE];
}

/**
 * Derive the launch runtime (HarnessId) from a model's PROVIDER.
 *
 * Native-only: EVERY provider maps to 'native'. The native harness is the
 * multi-model arm — it picks the transport from the MODEL's wire dialect
 * (sandbox `selectNativeTransport`), so a GPT model still rides the OpenAI
 * Responses transport under runtime='native'. This previously returned
 * 'openai_responses' for GPT, which was the last surface minting a legacy
 * runtime id onto tasks.runtime.
 *
 * 'google' (Gemini) joined that shape on 2026-09-03 — it rides the Interactions
 * transport inside the same native arm, so no 'gemini' HarnessId was ever needed.
 */
export function providerToHarnessId(provider: ModelProvider): HarnessId {
  switch (provider) {
    case 'anthropic':
      return 'native';
    case 'openai':
      return 'native';
    case 'fireworks':
      // Fireworks (open models: GLM 5.2, DeepSeek V4, …) is native-only: it rides the
      // Chat Completions transport selected INSIDE the native harness
      // (selectNativeTransport off caps.api). So its launch runtime is the (ungated)
      // native harness — mirroring how 'anthropic' maps to 'native' — NOT the
      // flag-gated 'openai_responses' arm.
      return 'native';
    case 'xai':
    case 'deepseek':
      // Direct lanes (Grok / DeepSeek first-party) are native-only:
      // their transports are selected INSIDE the native harness off caps.api —
      // mirroring 'fireworks'.
      return 'native';
    case 'google':
      // Google (Gemini) rides the Interactions transport selected INSIDE the native
      // harness off caps.api — the same shape as every other provider (spec
      // 2026-09-03). The old "P2: add a 'gemini' HarnessId" was never needed:
      // native IS the multi-model arm.
      return 'native';
    default: {
      // Exhaustiveness guard — a future ModelProvider member must be mapped.
      const _exhaustive: never = provider;
      throw new Error(`providerToHarnessId: unmapped provider ${String(_exhaustive)}`);
    }
  }
}

/**
 * Which model PROVIDERS each harness (runtime) can run. The harness is the user's
 * EXPLICIT choice; the model is then picked from that harness's allowed set.
 *
 *   - Claude harness  → Anthropic models only
 *   - OpenAI harness  → OpenAI models only
 *   - Vestra (native) → the MULTI-MODEL harness: every model, with the transport
 *                       chosen per model provider inside the harness.
 *
 * This drives BOTH the picker's per-harness model list AND the server-side
 * "is this model allowed for the chosen harness" validation. The model NEVER
 * changes the harness — the harness gates the models. (google/Gemini joined
 * native's set on 2026-09-03 with the gemini_interactions transport.)
 */
export const PROVIDERS_BY_HARNESS: Record<HarnessId, readonly ModelProvider[]> = {
  claude: ['anthropic'],
  openai: ['openai'],
  openai_responses: ['openai'],
  native: ['anthropic', 'openai', 'fireworks', 'xai', 'deepseek', 'google'],
  // The Claude Code CLI only ever talks to Anthropic — it is the user's own Claude
  // plan, so no other provider is reachable from it.
  claude_code: ['anthropic'],
  // The Codex CLI only ever talks to OpenAI, on the user's own ChatGPT plan. Narrower than
  // the provider: only the ids on ENGINE_MODEL_IDS.codex (engines/models.ts).
  codex: ['openai'],
} as const;

/** True when a model of `provider` may run on `harness` (see PROVIDERS_BY_HARNESS). */
export function isModelAllowedForHarness(provider: ModelProvider, harness: HarnessId): boolean {
  return PROVIDERS_BY_HARNESS[harness].includes(provider);
}

/**
 * True when the model `modelId` names may run on `harness`: its provider passes the harness's gate
 * and, for an engine that lists its models by id (Codex, engines/models.ts), the id is on that list.
 * For every other harness this is exactly isModelAllowedForHarness of the id's provider.
 */
export function isModelAllowedOnEngine(modelId: string, harness: HarnessId): boolean {
  const caps = resolveCapabilities(modelId);
  return isModelAllowedForHarness(caps.provider, harness) && engineAllowsModel(harness, caps.id);
}

/**
 * Whether a model id a task, a routine or a request names may be pinned on `harness`: the
 * registry knows it (its canonical id, so a dated or aliased id counts) and the harness
 * runs its provider. An id the registry does not know (a model whose provider was removed,
 * a typo) resolves to a GUESSED provider, so pinning it would send the request to a vendor
 * that has never heard of it. Every reader asks this one question and, on false, runs its
 * unpinned default instead.
 */
export function isModelPinnable(modelId: string, harness: HarnessId): boolean {
  return KNOWN_MODEL_IDS.includes(resolveCapabilities(modelId).id) && isModelAllowedOnEngine(modelId, harness);
}
